/**
 * Blocklist checks ported from Android's BlacklistUtils.kt (PULS-1383, PULS-1405), so the web
 * labels, validates and de-duplicates entries the same way the phone does.
 */

const hasLetter = text => /\p{L}/u.test(text);
const hasDigit = text => /\d/.test(text);
const hasWhitespace = text => /\s/.test(text);

/**
 * Whether text typed into the "block a phone number" field cannot be a phone number, and so was
 * probably meant as a phrase. Deliberately loose: it's used to ask, not to decide, because a short
 * letters-only string could also be a sender ID like HP-BHKPOS.
 */
export function isPhraseLike (input) {
    const trimmed = input.trim();
    if (!hasLetter(trimmed)) {
        // Some formatting of a phone number, e.g. "515 991 1493".
        return false;
    }

    return hasWhitespace(trimmed) || !hasDigit(trimmed);
}

// PhoneNumberUtils.clearFormattingAndStripStandardReplacements(): numbers with letters (sender IDs)
// and email addresses are kept as typed; anything else keeps only its dialable characters.
function clearFormatting (number) {
    let cleared = number;
    if (!hasLetter(number) && number.indexOf('@') === -1) {
        cleared = number.replace(/[^0-9+*#,;]/g, '');
    }

    return cleared.replace(/-/g, '').replace(/ /g, '').replace(/\(/g, '').replace(/\)/g, '');
}

// One number's slot of SmsMmsUtils.createIdMatcher(): the last `length` characters.
function lastCharacters (number, length) {
    if (number.indexOf('@') !== -1 || number.length < length) {
        return number;
    }

    return number.substring(number.length - length);
}

/** BlacklistUtils.numbersMatch(): "515-991-1493", "+15159911493" and "5159911493" all match. */
export function numbersMatch (number, blacklisted) {
    if (number === blacklisted) {
        return true;
    }

    const one = clearFormatting(number);
    const other = clearFormatting(blacklisted);
    const shorterLength = Math.min(one.length, other.length);

    if (shorterLength >= 10) {
        return lastCharacters(one, 10) === lastCharacters(other, 10);
    } else if (shorterLength >= 8) {
        return lastCharacters(one, 8) === lastCharacters(other, 8);
    } else if (shorterLength === 7) {
        return lastCharacters(one, 7) === lastCharacters(other, 7);
    } else if (one.length === other.length) {
        return lastCharacters(one, 5) === lastCharacters(other, 5);
    }

    return false;
}

/**
 * BlacklistUtils.isSameEntry(): whether two entries block the same thing. Numbers compare as phone
 * numbers, phrases ignoring case, and a number never equals a phrase.
 */
export function isSameEntry (one, other) {
    const oneNumber = (one.phone_number || '').trim();
    const otherNumber = (other.phone_number || '').trim();
    if (oneNumber && otherNumber) {
        return numbersMatch(oneNumber, otherNumber);
    }

    const onePhrase = (one.phrase || '').trim();
    const otherPhrase = (other.phrase || '').trim();
    if (onePhrase && otherPhrase) {
        return onePhrase.toLowerCase() === otherPhrase.toLowerCase();
    }

    return false;
}

/** DataSource.findEquivalentBlacklist(): the existing entry that already blocks `entry`, or null. */
export function findEquivalentEntry (entries, entry) {
    return (entries || []).find(existing => isSameEntry(existing, entry)) || null;
}

// CSV in the same format as Android's BlacklistCsvUtils (PULS-945), so files move between the two.
const CSV_HEADER = 'phone_number,phrase';

function escapeCsvField (value) {
    if (!value) {
        return '';
    }

    const normalized = value.replace(/\n/g, ' ');
    if (normalized.indexOf(',') !== -1 || normalized.indexOf('"') !== -1) {
        return '"' + normalized.replace(/"/g, '""') + '"';
    }

    return normalized;
}

function parseCsvLine (line) {
    const fields = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQuotes && c === '"' && line[i + 1] === '"') {
            current += '"';
            i++;
        } else if (c === '"') {
            inQuotes = !inQuotes;
        } else if (c === ',' && !inQuotes) {
            fields.push(current);
            current = '';
        } else {
            current += c;
        }
    }
    fields.push(current);

    return fields;
}

export function toCsv (entries) {
    let csv = CSV_HEADER + '\n';
    for (const entry of entries) {
        csv += escapeCsvField(entry.phone_number) + ',' + escapeCsvField(entry.phrase) + '\n';
    }

    return csv;
}

function isHeader (line) {
    const cols = parseCsvLine(line).map(col => col.trim().toLowerCase());
    return cols[0] === 'phone_number' && (cols[1] || '') === 'phrase';
}

/**
 * Entries as { phone_number } or { phrase }. Unlike Android, which always skips the first line, the
 * header is only skipped when it is one, so a file of bare numbers keeps its first entry. A row with
 * both columns becomes two entries, since each blocklist entry blocks either a number or a phrase.
 */
export function fromCsv (csv) {
    const result = [];
    const lines = csv.replace(/\r/g, '').split('\n');
    const start = lines.length > 0 && isHeader(lines[0]) ? 1 : 0;

    for (let i = start; i < lines.length; i++) {
        if (lines[i].trim() === '') {
            continue;
        }

        const cols = parseCsvLine(lines[i]);
        const phone = (cols[0] || '').trim();
        const phrase = (cols[1] || '').trim();
        if (phone === '' && phrase === '') {
            continue;
        }

        if (phone !== '') result.push({ phone_number: phone });
        if (phrase !== '') result.push({ phrase });
    }

    return result;
}
